"use client"

import React, { memo, useEffect, useMemo } from "react"
import { useDispatch, useSelector } from "react-redux"
import { makeStyles } from "tss-react/mui"
import {
  IconButton,
  Tooltip,
  Avatar,
  ListItemAvatar,
  ListItemText,
  ListItemButton,
  Typography,
  Box,
} from "@mui/material"
import {
  DateRangeRounded,
  Speed,
  Anchor,
  Lock,
  SatelliteAlt,
  Sensors,
  Stop,
  Navigation,
  Battery20,
  Battery30,
  Battery60,
  Battery80,
  BatteryFull,
  BatteryChargingFull,
  Key,
} from "@mui/icons-material"
// Import gauge and GPS-related icons. These SVGs are preloaded via vite/webpack as React components.
import GaugeMin from '../resources/images/data/gauge-min.svg?react'
import GaugeLow from '../resources/images/data/gauge-low.svg?react'
import GaugeHigh from '../resources/images/data/gauge-high.svg?react'
import GaugeMax from '../resources/images/data/gauge-max.svg?react'
import NoGPS from '../resources/images/data/no-gps.svg?react'
import WifiIcon from '@mui/icons-material/Wifi'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBell, faCarBattery } from "@fortawesome/free-solid-svg-icons"
import dayjs from "dayjs"
import relativeTime from "dayjs/plugin/relativeTime"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import duration from "dayjs/plugin/duration"
import { formatAlarm, formatBoolean, formatSpeed, formatDate } from "../common/util/formatter"
import { devicesActions, stoppedActions } from "../store"
import { useTranslation } from "../common/components/LocalizationProvider"
import { useAdministrator } from "../common/util/permissions"
import { mapIconKey, mapIcons } from "../map/core/preloadImages"
import { useAttributePreference } from "../common/util/preferences"
import { alpha } from "@mui/material/styles"

dayjs.extend(relativeTime)
dayjs.extend(utc)
dayjs.extend(timezone)
dayjs.extend(duration)

const useStyles = makeStyles()((theme) => ({
  categoryIcon: { width: "25px", height: "25px", filter: "brightness(0) invert(1)" },
  statusIconsRow: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.4),
    paddingLeft: theme.spacing(5),
    marginTop: theme.spacing(-0.5),
    paddingBottom: theme.spacing(0.5),
    flexWrap: "nowrap",
    alignItems: "center",
    minHeight: "32px",
  },
  statusIconsPlaceholder: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.4),
    paddingLeft: theme.spacing(5),
    marginTop: theme.spacing(-0.5),
    paddingBottom: theme.spacing(0.5),
    flexWrap: "nowrap",
    alignItems: "center",
    minHeight: "32px",
    backgroundColor: "transparent",
  },
  statusIcon: { fontSize: "1.3rem !important", verticalAlign: "middle" },
  iconContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.3),
    padding: theme.spacing(0.2),
    margin: "0 !important",
    minWidth: "auto",
    height: "24px",
  },
  iconLabel: { fontSize: "0.65rem", fontWeight: 500, lineHeight: 1, whiteSpace: "nowrap" },
  // Apply both `color` and `fill` so that custom SVG components (e.g. gauge icons)
  // inherit the correct colour. Without setting `fill`, imported SVGs may not
  // respond to the colour classes defined here.
  success: { color: theme.palette.success.main, fill: theme.palette.success.main },
  warning: { color: theme.palette.warning.main, fill: theme.palette.warning.main },
  error: { color: theme.palette.error.main, fill: theme.palette.error.main },
  neutral: { color: theme.palette.neutral.main, fill: theme.palette.neutral.main },
  // Add an info color helper so that battery and other info states can
  // leverage the palette.info token defined in the theme. Without this
  // definition the code references classes.info would be undefined.
  info: { color: theme.palette.info.main, fill: theme.palette.info.main },
  deviceRowContainer: {
    padding: theme.spacing(0.5, 0, 0.5, 0),
    overflow: "visible",
    minHeight: "auto",
    height: "100px",
    position: "relative",
    marginBottom: theme.spacing(0.5),
  },
  deviceTextContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    minWidth: 0,
  },
  deviceName: {
    fontWeight: "bold",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: "0.95rem",
    flex: 1,
    minWidth: 0,
  },
  plateText: {
    fontSize: "0.7rem",
    color: (theme) => theme.palette.text.secondary,
    opacity: 0.8,
    fontWeight: "bold",
    lineHeight: 1.2,
    marginLeft: theme.spacing(1),
    flexShrink: 0,
  },
}))

const DeviceRow = memo(({ device, position }) => {
  const item = device
  if (!item || typeof item !== "object") return null

  const { classes } = useStyles()
  const dispatch = useDispatch()
  const t = useTranslation()
  const admin = useAdministrator()
  const speedUnit = useAttributePreference("speedUnit")
  const selectedDeviceId = useSelector((state) => state.devices.selectedId)
  const dateTimeFormat = useAttributePreference("dateTimeFormat", "YYYY-MM-DD HH:mm:ss")
  const devicePrimary = useAttributePreference("devicePrimary", "name")

  const stoppedSince = useSelector((state) => state.stopped.byDeviceId[item.id] || null)

  // ---------------------------------------------------------------
  // Compute the amount of time the device has been stopped using a
  // memoized value. This prevents recalculating the duration on every
  // render unless the source timestamp (`stoppedSince`) changes.
  const stoppedTime = useMemo(() => {
    if (!stoppedSince) return null
    const now = dayjs()
    const stoppedAt = dayjs(stoppedSince)
    const diff = dayjs.duration(now.diff(stoppedAt))
    if (diff.asDays() >= 1) {
      const days = Math.floor(diff.asDays())
      return `${days} ${days > 1 ? "dias" : "dia"}`
    }
    const hours = diff.hours()
    const minutes = diff.minutes()
    return `${hours > 0 ? `${hours}h ` : ""}${minutes}min`
  }, [stoppedSince])

  useEffect(() => {
    if (!position) return
    if (position.attributes?.motion === false && position.fixTime) {
      if (!stoppedSince) {
        dispatch(stoppedActions.setStoppedSince({ deviceId: item.id, timestamp: position.fixTime }))
      }
    } else {
      if (stoppedSince) {
        dispatch(stoppedActions.clearStoppedSince({ deviceId: item.id }))
      }
    }
  }, [position?.attributes?.motion, position?.fixTime])



  const secondaryText = () => {
    const hasPositionData = position && position.fixTime
    const hasSpeedData = position && position.speed !== undefined
    // Use the memoized stoppedTime computed via useMemo. Do not shadow the outer
    // variable to avoid confusion.

    return (
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", width: "100%", gap: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flex: 1, pr: 1 }}>
          <DateRangeRounded fontSize="small" color="disabled" />
          <Typography variant="body2" noWrap sx={{ color: hasPositionData ? "text.primary" : "text.disabled", fontSize: "0.8rem" }}>
            {hasPositionData ? formatDate(position.fixTime, dateTimeFormat) : t("sharedNoData")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.2 }}>
          {/* Right-aligned container for the speed indicator and "Parado" message. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {/* Show a gauge icon representing the current speed. Only show when online; choose icon and color based on thresholds. */}
            {(() => {
              // Determine which gauge icon to show and its colour class. Use speed 0 as fallback.
              const speedValue = position?.speed || 0
              // Compose tooltip content for the gauge icon.
              const speedTooltip = `${t('positionSpeed')}: ${formatSpeed(speedValue, speedUnit, t)}`
              // Only render a gauge icon when the device is online; otherwise omit.
              if (item.status === 'online') {
                if (speedValue > 50) {
                  return (
                    <Tooltip title={speedTooltip}>
                      <IconButton size="small" sx={{ minWidth: '28px', padding: '4px' }} aria-label={speedTooltip}>
                        <GaugeMax className={`${classes.statusIcon} ${classes.error}`} width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  )
                } else if (speedValue > 25) {
                  return (
                    <Tooltip title={speedTooltip}>
                      <IconButton size="small" sx={{ minWidth: '28px', padding: '4px' }} aria-label={speedTooltip}>
                        <GaugeHigh className={`${classes.statusIcon} ${classes.success}`} width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  )
                } else if (speedValue > 0) {
                  return (
                    <Tooltip title={speedTooltip}>
                      <IconButton size="small" sx={{ minWidth: '28px', padding: '4px' }} aria-label={speedTooltip}>
                        <GaugeLow className={`${classes.statusIcon} ${classes.success}`} width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  )
                } else if (position?.attributes?.ignition) {
                  return (
                    <Tooltip title={speedTooltip}>
                      <IconButton size="small" sx={{ minWidth: '28px', padding: '4px' }} aria-label={speedTooltip}>
                        <GaugeMin className={`${classes.statusIcon} ${classes.warning}`} width={20} height={20} />
                      </IconButton>
                    </Tooltip>
                  )
                }
                // If none of the thresholds match (e.g. speed 0 and ignition off), omit the gauge icon.
              }
              return null
            })()}

            {/* Always display the numeric speed next to the gauge icon, if speed data is available. */}
            <Typography variant="body2" sx={{ color: hasSpeedData ? 'text.primary' : 'text.disabled', fontSize: '0.8rem' }}>
              {`${formatSpeed(position?.speed || 0, speedUnit, t)}`}
            </Typography>
          </Box>

          {position?.attributes?.motion === false && stoppedTime && (
            <Typography variant="body2" sx={{ color: 'warning.main', fontWeight: 500, fontSize: '0.7rem' }}>
              ⛔ Parado há {stoppedTime}
            </Typography>
          )}
        </Box>
      </Box>
    )
  }

  return (
    <div className={classes.deviceRowContainer}>
      <ListItemButton
        key={item.id}
        onClick={() => dispatch(devicesActions.selectId(item.id))}
        disabled={!admin && item.disabled}
        selected={selectedDeviceId === item.id}
        sx={{
          borderRadius: 1,
          transition: "all 0.2s ease",
          overflow: "hidden",
          margin: "2px 4px",
          border: "2px solid",
          borderColor: (theme) =>
            selectedDeviceId === item.id
              ? item.status === "online"
                ? theme.palette.success.main
                : item.status === "offline"
                ? theme.palette.error.main
                : theme.palette.warning.main
              : item.status === "online"
              ? theme.palette.mode === "dark"
                ? alpha(theme.palette.success.light, 0.8)
                : alpha(theme.palette.success.dark, 0.8)
              : item.status === "offline"
              ? theme.palette.mode === "dark"
                ? alpha(theme.palette.error.light, 0.8)
                : alpha(theme.palette.error.dark, 0.8)
              : theme.palette.mode === "dark"
              ? alpha(theme.palette.warning.light, 0.8)
              : alpha(theme.palette.warning.dark, 0.8),
          padding: "4px 8px",
          minHeight: "auto",
          height: "96px",
          boxSizing: "border-box",
          backgroundColor: (theme) =>
            selectedDeviceId === item.id
              ? alpha(
                  item.status === "online"
                    ? theme.palette.success.main
                    : item.status === "offline"
                    ? theme.palette.error.main
                    : theme.palette.warning.main,
                  0.3
                )
              : item.status === "online"
              ? theme.palette.mode === "dark"
                ? alpha(theme.palette.success.dark, 0.12)
                : alpha(theme.palette.success.light, 0.12)
              : item.status === "offline"
              ? theme.palette.mode === "dark"
                ? alpha(theme.palette.error.dark, 0.12)
                : alpha(theme.palette.error.light, 0.12)
              : theme.palette.mode === "dark"
              ? alpha(theme.palette.warning.dark, 0.12)
              : alpha(theme.palette.warning.light, 0.12),
        }}
      >
        <Box sx={{ width: "100%" }}>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <ListItemAvatar>
              <Avatar>
                <img className={classes.categoryIcon} src={mapIcons[mapIconKey(item.category)] || "/placeholder.svg"} alt="" />
              </Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={
                <Box className={classes.deviceTextContainer}>
                  <Tooltip title={item[devicePrimary]}>
                    <Typography className={classes.deviceName} noWrap>{item[devicePrimary]}</Typography>
                  </Tooltip>
                  {item.attributes?.form?.plate && (
                    <Typography className={classes.plateText}>{item.attributes.form.plate}</Typography>
                  )}
                </Box>
              }
              secondary={secondaryText()}
              secondaryTypographyProps={{ component: "div" }}
            />
          </Box>

          {position ? (
            <Box className={classes.statusIconsRow}>
              <Tooltip title={item.status === "online" ? t("statusOnline") : t("statusOffline")}> 
                <IconButton size="small" sx={{ minWidth: "28px", padding: "4px" }}
                  aria-label={item.status === "online" ? t("statusOnline") : t("statusOffline")}
                >
                  <Sensors
                    className={`${classes.statusIcon} ${item.status === "online" ? classes.success : classes.error}`}
                  />
                </IconButton>
              </Tooltip>

              <Tooltip title={`${t("satellites")}: ${position.attributes.sat || 0}`}> 
                <IconButton size="small" sx={{ minWidth: "28px", padding: "4px" }}
                  aria-label={`${t("satellites")}: ${position.attributes.sat || 0}`}
                >
                  <SatelliteAlt
                    className={`${classes.statusIcon} ${position.attributes.sat > 0 ? classes.success : classes.error}`}
                  />
                </IconButton>
              </Tooltip>

              {/* Show approximate indicator when present. If position.attributes.approximate is truthy, use success color; otherwise neutral. */}
              {position.attributes.hasOwnProperty('approximate') && (
                <Tooltip title={`${t('positionApproximate')}: ${formatBoolean(position.attributes.approximate, t)}`}>
                  <IconButton size="small" sx={{ minWidth: "28px", padding: "4px" }}
                    aria-label={`${t('positionApproximate')}: ${formatBoolean(position.attributes.approximate, t)}`}
                  >
                    {position.attributes.approximate ? (
                      <WifiIcon className={`${classes.statusIcon} ${classes.success}`} sx={{ width: 20, height: 20 }} />
                    ) : (
                      <WifiIcon className={`${classes.statusIcon} ${classes.neutral}`} sx={{ width: 20, height: 20 }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}

              {/* Gauge icons for speed are now rendered within the secondary text rather than the status icons row. */}

              {/* Show invalid position icon when the reported position is not valid. */}
              {position && !position.valid && (
                <Tooltip title={`${t('positionInvalid')}`}>
                  <IconButton size="small" sx={{ minWidth: '28px', padding: '4px' }}
                    aria-label={t('positionInvalid')}
                  >
                    <NoGPS className={`${classes.statusIcon} ${classes.error}`} width={20} height={20} />
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip title={position.attributes.motion ? t("moving") : t("stopped")}> 
                <IconButton size="small" sx={{ minWidth: "28px", padding: "4px" }}
                  aria-label={position.attributes.motion ? t("moving") : t("stopped")}
                >
                  {position.attributes.motion ? (
                    <Navigation className={`${classes.statusIcon} ${classes.success}`} />
                  ) : (
                    <Stop className={`${classes.statusIcon} ${classes.neutral}`} />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title={item.attributes?.hasAnchor ? t("anchorActive") : t("anchorInactive")}> 
                <IconButton size="small" sx={{ minWidth: "28px", padding: "4px" }}
                  aria-label={item.attributes?.hasAnchor ? t("anchorActive") : t("anchorInactive")}
                >
                  <Anchor
                    className={`${classes.statusIcon} ${
                      !item.attributes?.hasAnchor
                        ? classes.neutral // Cinza quando não tem âncora ativa
                        : item.attributes?.lockOnExit
                          ? classes.error // Vermelho quando âncora ativa e aciona bloqueio ao sair
                          : classes.success // Verde quando âncora ativa e não aciona bloqueio
                    }`}
                  />
                </IconButton>
              </Tooltip>

              {position.attributes.hasOwnProperty("ignition") && (
                <Tooltip title={formatBoolean(position.attributes.ignition, t)}>
                  <IconButton
                    size="small"
                    sx={{
                      minWidth: "28px",
                      padding: "4px",
                      color: (theme) =>
                        position.attributes.ignition ? theme.palette.success.main : theme.palette.error.main,
                    }}
                    aria-label={formatBoolean(position.attributes.ignition, t)}
                  >
                    <Key
                      sx={{ transform: "rotate(90deg)" }}
                      className={`${classes.statusIcon} ${
                        position.attributes.ignition ? classes.success : classes.error
                      }`}
                    />
                  </IconButton>
                </Tooltip>
              )}

              {position.attributes.hasOwnProperty("batteryLevel") && (
                <Tooltip
                  title={
                    position.attributes.batteryLevel !== undefined && position.attributes.batteryLevel !== null
                      ? `Bateria: ${Math.min(100, Math.floor(Number(position.attributes.batteryLevel) || 0))}%${position.attributes.charge ? " (Carregando)" : ""}`
                      : "Bateria: N/A"
                  }
                >
                  <IconButton size="small" sx={{ minWidth: "auto", padding: "4px" }}
                    aria-label={
                      position.attributes.batteryLevel !== undefined && position.attributes.batteryLevel !== null
                        ? `Bateria: ${Math.min(100, Math.floor(Number(position.attributes.batteryLevel) || 0))}%${position.attributes.charge ? " (Carregando)" : ""}`
                        : "Bateria: N/A"
                    }
                  >
                    <Box className={classes.iconContainer}>
                      {(() => {
                        const batteryLevel = Math.min(
                          100,
                          Math.max(0, Math.floor(Number(position.attributes.batteryLevel)) || 0),
                        )
                        const isCharging = position.attributes.charge
                        const isFull = batteryLevel >= 100
                        const isFullAndCharging = isFull && isCharging

                        const Icon = isCharging
                          ? BatteryChargingFull
                          : batteryLevel <= 20
                            ? Battery20
                            : batteryLevel <= 40
                              ? Battery30
                              : batteryLevel <= 60
                                ? Battery60
                                : batteryLevel <= 80
                                  ? Battery80
                                  : BatteryFull

                        return (
                          <>
                            <Icon
                              className={`${classes.statusIcon} ${
                                isFullAndCharging
                                  ? classes.info
                                  : batteryLevel < 30
                                    ? classes.error
                                    : batteryLevel < 50
                                      ? classes.warning
                                      : classes.success
                              }`}
                            />
                            <Typography className={classes.iconLabel}>
                              {`${Math.min(100, Math.floor(Number(position.attributes.batteryLevel) || 0))}%`}
                            </Typography>
                          </>
                        )
                      })()}
                    </Box>
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip
                title={`${position.attributes.power?.toFixed(1) || position.attributes.voltage?.toFixed(1) || position.attributes.battery?.toFixed(1) || position.attributes.adc1?.toFixed(1) || "0.0"}V`}
              >
                <IconButton size="small" sx={{ minWidth: "auto", padding: "4px" }}
                  aria-label={`${position.attributes.power?.toFixed(1) || position.attributes.voltage?.toFixed(1) || position.attributes.battery?.toFixed(1) || position.attributes.adc1?.toFixed(1) || "0.0"}V`}
                >
                  <Box className={classes.iconContainer}>
                    <FontAwesomeIcon
                      icon={faCarBattery}
                      className={`${classes.statusIcon} ${
                        (position.attributes.power > 0 || position.attributes.voltage > 0 || position.attributes.battery > 0 || position.attributes.adc1 > 0)
                          ? classes.success
                          : classes.neutral
                      }`}
                    />
                    <Typography className={classes.iconLabel}>
                      {position.attributes.power?.toFixed(1) ||
                       position.attributes.voltage?.toFixed(1) ||
                       position.attributes.battery?.toFixed(1) ||
                       position.attributes.adc1?.toFixed(1) || "0.0"}V
                    </Typography>
                  </Box>
                </IconButton>
              </Tooltip>

              {position.attributes.hasOwnProperty("alarm") && (
                <Tooltip title={formatAlarm(position.attributes.alarm, t)}>
                  <IconButton size="small" sx={{ minWidth: "28px", padding: "4px" }}
                    aria-label={formatAlarm(position.attributes.alarm, t)}
                  >
                    <FontAwesomeIcon icon={faBell} className={`${classes.statusIcon} ${classes.error} fa-shake`} />
                  </IconButton>
                </Tooltip>
              )}

              {position.attributes.hasOwnProperty("blocked") && (
                <Tooltip title={position.attributes.blocked ? t("blocked") : t("unlocked")}>
                  <IconButton size="small" sx={{ minWidth: "28px", padding: "4px" }}
                    aria-label={position.attributes.blocked ? t("blocked") : t("unlocked")}
                  >
                    <Lock
                      className={`${classes.statusIcon} ${
                        position.attributes.blocked ? classes.error : classes.success
                      }`}
                    />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          ) : (
            <Box className={classes.statusIconsPlaceholder} />
          )}
        </Box>
      </ListItemButton>
    </div>
  )
})

DeviceRow.displayName = "DeviceRow"
export default DeviceRow
